import React, { createContext, useContext, useState, useCallback } from 'react';
import { getData } from '../services/apiService';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
}

interface Company {
  id: number;
  name: string;
}

interface AdminData {
  users: User[];
  companies: Company[];
  lastUpdated: Date | null;
  isLoading: boolean;
}

interface AdminDataContextType extends AdminData {
  refreshUsers: () => Promise<void>;
  refreshCompanies: () => Promise<void>;
  refreshAll: () => Promise<void>;
}

const AdminDataContext = createContext<AdminDataContextType | undefined>(undefined);

export const useAdminData = () => {
  const context = useContext(AdminDataContext);
  if (context === undefined) {
    throw new Error('useAdminData must be used within an AdminDataProvider');
  }
  return context;
};

interface AdminDataProviderProps {
  children: React.ReactNode;
}

export const AdminDataProvider: React.FC<AdminDataProviderProps> = ({ children }) => {
  const [adminData, setAdminData] = useState<AdminData>({
    users: [],
    companies: [],
    lastUpdated: null,
    isLoading: false
  });

  const refreshUsers = useCallback(async () => {
    try {
      setAdminData(prev => ({ ...prev, isLoading: true }));
      const data = await getData('/api/users', { enableDeduplication: true });
      if (data.status === 'success') {
        setAdminData(prev => ({
          ...prev,
          users: data.data,
          lastUpdated: new Date(),
          isLoading: false
        }));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setAdminData(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const refreshCompanies = useCallback(async () => {
    try {
      setAdminData(prev => ({ ...prev, isLoading: true }));
      const data = await getData('/api/companies', { enableDeduplication: true });
      if (data.status === 'success') {
        setAdminData(prev => ({
          ...prev,
          companies: data.data,
          lastUpdated: new Date(),
          isLoading: false
        }));
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setAdminData(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const refreshAll = useCallback(async () => {
    try {
      setAdminData(prev => ({ ...prev, isLoading: true }));
      const [usersData, companiesData] = await Promise.all([
        getData('/api/users', { enableDeduplication: true }),
        getData('/api/companies', { enableDeduplication: true })
      ]);

      setAdminData({
        users: usersData.status === 'success' ? usersData.data : [],
        companies: companiesData.status === 'success' ? companiesData.data : [],
        lastUpdated: new Date(),
        isLoading: false
      });
    } catch (error) {
      console.error('Error fetching admin data:', error);
      setAdminData(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const value: AdminDataContextType = {
    ...adminData,
    refreshUsers,
    refreshCompanies,
    refreshAll
  };

  return (
    <AdminDataContext.Provider value={value}>
      {children}
    </AdminDataContext.Provider>
  );
};